import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Typo } from "@ui/text/Typo";
import Decimal from "decimal.js";
import QRCode from "qrcode";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GD } from "src/GD";
import { formatCryptoMinorUnits, getCheapestUsableCryptoCoinKey, parseUsdCents } from "src/utils/cryptoFees";
import { getFriendlyErrorMessage } from "src/utils/errors";
import { getCardHolderRecovery } from "src/utils/cardHolderRecovery";
import { DS, Section } from "../../ds/Kit";
import { formatPrivateMoney } from "../money";
import { nativeSelectStyle } from "../nativeSelectStyle";
import { Callout, EmptyState, PageIntro, PrivateRequestError, PrivateSurface } from "../ui";
import { useOptionalIssuer } from "../../issuer/IssuerProvider";
import { openSimplifiedKyc } from "../../components/SimplifiedKyc";

const formatMoney = (amount: number) => new Decimal(amount || 0).div(100).toFixed(2);
const CARD_HOLDER_INCOMPLETE = 2201;
const FUNDING_KYC_REQUIRED = 2711;
const NETWORK_UNAVAILABLE = 2712;
const QUOTE_MAX_AGE_MS = 120_000;

const SWEEP_COPY: Record<ICryptoDeposit["sweep_status"], string> = {
	none: "Received by issuer",
	waiting: "Waiting for the minimum",
	detected: "Detected",
	funding: "Preparing network fee",
	sweeping: "Forwarding",
	swept: "Forwarded",
	failed: "Action required",
};

const isDepositActive = (deposit: ICryptoDeposit) =>
	deposit.status === "pending" && !["none", "failed"].includes(deposit.sweep_status);

const showsProcessingEstimate = (deposit: ICryptoDeposit) =>
	deposit.status === "pending" &&
	!["waiting", "failed"].includes(deposit.sweep_status) &&
	!deposit.sweep_error &&
	!deposit.credit_error;

export const PrivateFunding = () => {
	const issuerContext = useOptionalIssuer();
	const cryptoEnabled = !issuerContext || issuerContext.selectedIssuer?.crypto_topup === true;
	const refreshIssuerAccounts = issuerContext?.refreshIssuers;
	const issuer = issuerContext?.selectedIssuerId ?? undefined;
	const [coins, setCoins] = useState<ICryptoCoin[]>([]);
	const [selected, setSelected] = useState<string>("");
	const [address, setAddress] = useState<ICryptoAddress | null>(null);
	const [qr, setQr] = useState("");
	const [deposits, setDeposits] = useState<ICryptoDeposit[]>([]);
	const [depositsLoaded, setDepositsLoaded] = useState(false);
	const [depositAmount, setDepositAmount] = useState("");
	const [quote, setQuote] = useState<ICryptoQuote | null>(null);
	const [quoteReceivedAt, setQuoteReceivedAt] = useState(0);
	const [quoteLoading, setQuoteLoading] = useState(false);
	const [quoteError, setQuoteError] = useState<{ message: string; code: number } | null>(null);
	const [loading, setLoading] = useState(true);
	const [addressLoading, setAddressLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [addressError, setAddressError] = useState<{ message: string; recovery: "phone" | "kyc" | "error" } | null>(null);
	const addressRequestRef = useRef(0);
	const quoteRequestRef = useRef(0);
	const flowRef = useRef(0);

	const load = useCallback(async () => {
		setLoading(true);
		setLoadError(null);
		try {
			const [coinsRes, depositsRes] = await Promise.all([GD.REQ_CRYPTO_COINS.request(), GD.REQ_CRYPTO_DEPOSITS.request()]);
			if (coinsRes.data) setCoins(coinsRes.data);
			if (depositsRes.data) { setDeposits(depositsRes.data); setDepositsLoaded(true); }
			const requestErrors = [
				coinsRes.error ? getFriendlyErrorMessage(coinsRes.error, "Crypto deposits are unavailable") : null,
				depositsRes.error ? getFriendlyErrorMessage(depositsRes.error, "Could not load recent deposits") : null
			].filter((message): message is string => Boolean(message));
			if (requestErrors.length) setLoadError([...new Set(requestErrors)][0]);
		} catch (error) {
			setLoadError(getFriendlyErrorMessage(error, "Crypto deposits are unavailable"));
		} finally {
			setLoading(false);
		}
	}, []);

	const refreshDeposits = useCallback(async (showError = true) => {
		try {
			const res = await GD.REQ_CRYPTO_DEPOSITS.request();
			if (res.data) {
				setDeposits(res.data);
				setDepositsLoaded(true);
				if (showError) {
					if (refreshIssuerAccounts) await refreshIssuerAccounts();
					else await GD.REQ_COMPANY_REFRESH.request();
				}
			}
			else if (showError && res.error) toast.error(getFriendlyErrorMessage(res.error, "Could not refresh deposits"));
		} catch (error) {
			if (showError) toast.error(getFriendlyErrorMessage(error, "Could not refresh deposits"));
		}
	}, [refreshIssuerAccounts]);

	const recoverFromGate = useCallback(async (error: IError, retry: () => Promise<unknown>, flowId: number) => {
		const recovery = error.code === CARD_HOLDER_INCOMPLETE ? getCardHolderRecovery(error) : error.code === FUNDING_KYC_REQUIRED ? "kyc" : "error";
		if (recovery === "phone") {
			const { openKycPhoneForm } = await import("../../components/KycPhoneForm");
			if (flowId === flowRef.current) openKycPhoneForm(error.issuer ?? issuer, async () => { if (flowId === flowRef.current) await retry(); }, { title: "Complete card holder details" });
		} else if (recovery === "kyc" && flowId === flowRef.current) {
			openSimplifiedKyc(async () => { if (flowId === flowRef.current) await retry(); return { data: null }; }, undefined, { ...error, issuer: error.issuer ?? issuer }, {
				purpose: "funding",
				forceOurKycRestart: error.code === CARD_HOLDER_INCOMPLETE,
			});
		}
		return recovery;
	}, [issuer]);

	const requestQuote = useCallback(async (coinKey: string, amount: number, flowId: number, recover = true): Promise<ICryptoQuote | null> => {
		const requestId = ++quoteRequestRef.current;
		setQuoteLoading(true);
		setQuoteError(null);
		setQuote(null);
		setAddress(null);
		setQr("");
		try {
			const res = await GD.REQ_CRYPTO_QUOTE.request({ coin_key: coinKey, amount });
			if (requestId !== quoteRequestRef.current || flowId !== flowRef.current) return null;
			if (res.error) {
				const message = res.error.code === NETWORK_UNAVAILABLE
					? "This network is temporarily unavailable. Select another asset or network."
					: getFriendlyErrorMessage(res.error, "Could not calculate the transfer amount");
				setQuoteError({ message: res.error.details ? `${message} ${res.error.details}` : message, code: res.error.code });
				if (recover && [CARD_HOLDER_INCOMPLETE, FUNDING_KYC_REQUIRED].includes(res.error.code)) {
					await recoverFromGate(res.error, () => requestQuote(coinKey, amount, flowId, false), flowId);
				}
				return null;
			}
			setQuote(res.data);
			setQuoteReceivedAt(Date.now());
			return res.data;
		} catch (error) {
			if (requestId === quoteRequestRef.current && flowId === flowRef.current) setQuoteError({ message: getFriendlyErrorMessage(error, "Could not calculate the transfer amount"), code: 0 });
			return null;
		} finally {
			if (requestId === quoteRequestRef.current && flowId === flowRef.current) setQuoteLoading(false);
		}
	}, [recoverFromGate]);

	const loadAddress = useCallback(async function requestAddress(coinKey: string, quoted: ICryptoQuote, flowId: number, recover = true): Promise<IAPIResponse<unknown>> {
		const requestId = ++addressRequestRef.current;
		setAddress(null);
		setQr("");
		setAddressError(null);
		setAddressLoading(true);
		try {
			const res = await GD.REQ_CRYPTO_ADDRESS.request({ coin_key: coinKey });
			if (requestId !== addressRequestRef.current || flowId !== flowRef.current) return { data: null };
			if (res.error) {
				const recovery = res.error.code === CARD_HOLDER_INCOMPLETE ? getCardHolderRecovery(res.error) : res.error.code === FUNDING_KYC_REQUIRED ? "kyc" : "error";
				const message = recovery === "phone"
					? "Complete your card holder details to get a deposit address."
					: recovery === "kyc"
						? "Complete identity verification before adding funds."
						: getFriendlyErrorMessage(res.error, "Could not get a deposit address");
				setAddressError({ message, recovery });
				// Recovery belongs to this exact selection/request. A closed page,
				// changed issuer or changed network must never retry the old request.
				const retry = async (): Promise<IAPIResponse<unknown>> => {
					if (requestId !== addressRequestRef.current || flowId !== flowRef.current) return { data: null };
					const refreshed = await requestQuote(coinKey, quoted.credit_cents, flowId, false);
					return refreshed && flowId === flowRef.current
						? requestAddress(coinKey, refreshed, flowId, false) : { data: null };
				};
				if (recover && recovery !== "error") await recoverFromGate(res.error, retry, flowId);
				else if (recovery === "error") {
					toast.error(message);
				}
				return res;
			}
			setAddress(res.data);
			try {
				const nextQr = await QRCode.toDataURL(res.data.address, { width: 220, margin: 1 });
				if (requestId === addressRequestRef.current && flowId === flowRef.current) setQr(nextQr);
			} catch {
				if (requestId === addressRequestRef.current && flowId === flowRef.current) setQr("");
			}
			return res;
		} catch (error) {
			const message = getFriendlyErrorMessage(error, "Could not get a deposit address");
			if (requestId === addressRequestRef.current && flowId === flowRef.current) {
				setAddress(null);
				setQr("");
				setAddressError({ message, recovery: "error" });
				toast.error(message);
			}
			return { error: { code: 0, message } };
		} finally {
			if (requestId === addressRequestRef.current && flowId === flowRef.current) setAddressLoading(false);
		}
	}, [recoverFromGate, requestQuote]);

	const showAddress = useCallback(async () => {
		const amount = parseUsdCents(depositAmount);
		if (!selected || amount === null || amount <= 0 || addressLoading) return;
		const flowId = flowRef.current;
		const freshQuote = quote && !quote.stale && quote.coin_key === selected && quote.credit_cents === amount && Date.now() - quoteReceivedAt < QUOTE_MAX_AGE_MS
			? quote : await requestQuote(selected, amount, flowId);
		if (!freshQuote || flowId !== flowRef.current) return;
		await loadAddress(selected, freshQuote, flowId);
	}, [addressLoading, depositAmount, loadAddress, quote, quoteReceivedAt, requestQuote, selected]);

	useEffect(() => {
		if (cryptoEnabled) void load();
	}, [cryptoEnabled, load]);
	useEffect(() => {
		if (!cryptoEnabled || !selected) return;
		const amount = parseUsdCents(depositAmount);
		if (amount === null || amount <= 0) return;
		const flowId = flowRef.current;
		const timer = window.setTimeout(() => void requestQuote(selected, amount, flowId), 500);
		return () => window.clearTimeout(timer);
	}, [cryptoEnabled, depositAmount, requestQuote, selected]);
	useEffect(() => {
		setQuote(null); setQuoteError(null); setQuoteLoading(false);
		setAddress(null); setQr(""); setAddressError(null); setAddressLoading(false);
		return () => { ++flowRef.current; ++quoteRequestRef.current; ++addressRequestRef.current; };
	}, [cryptoEnabled, issuer]);
	useEffect(() => {
		if (!deposits.some(isDepositActive)) return;
		const timer = window.setInterval(() => void refreshDeposits(false), 15_000);
		return () => window.clearInterval(timer);
	}, [deposits, refreshDeposits]);

	const cheapestCoinKey = getCheapestUsableCryptoCoinKey(coins);
	const selectedCoin = coins.find(coin => coin.coin_key === selected) ?? null;
	const amountCents = parseUsdCents(depositAmount);
	const qrPlaceholder = addressError
		? "Your QR code will appear here once the deposit address is available."
		: addressLoading ? "Generating your deposit QR code…" : "Choose the amount and show the deposit address to generate the QR code.";

	if (!cryptoEnabled) {
		return (
		<PrivateSurface sx={{ padding: "16px" }}>
			<EmptyState
				icon={<IconDeposit />}
				title="Crypto funding is not available for this issuer"
				description="Choose an issuer account with crypto funding enabled or contact support."
			/>
		</PrivateSurface>
		);
	}

	const activeStep = address ? 2 : selected ? 1 : 0;
	const inputStyle = { boxSizing: "border-box" as const, width: "100%", height: "44px", padding: "0 14px", borderRadius: "12px", border: `1px solid ${DS.line}`, backgroundColor: "#fff", color: DS.ink, fontSize: "14px", fontFamily: "inherit" };

	return (
		<VBox gap="16px">
			<PageIntro title="Add funds" description="Choose a coin and network, then send money to the deposit address." />
			<Stepper active={activeStep} steps={["Choose network", "Send funds", "Balance updated"]} />
			{loadError && <PrivateRequestError message={loadError} onRetry={load} />}
			<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(300px, .85fr)", gap: "16px", alignItems: "start", "@media (max-width: 900px)": { gridTemplateColumns: "minmax(0, 1fr)" } }}>
				<Section title="Select funding method">
					<VBox gap="18px" sx={{ padding: "4px 0 14px" }}>
						<VBox gap="6px">
							<Typo sx={{ color: DS.text, fontSize: "13px", fontWeight: 600 }}>Asset and network</Typo>
							<Box sx={{ position: "relative" }}>
								{selected ? <Box aria-hidden sx={{ position: "absolute", zIndex: 1, left: "10px", top: "8px", width: "28px", height: "28px", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", background: DS.gradient, color: "#fff", fontWeight: 700, fontSize: "0.82rem", pointerEvents: "none" }}>₮</Box> : null}
								<select
									aria-label="Asset and network"
									value={selected}
									disabled={loading}
									onChange={event => {
										const coin = coins.find(item => item.coin_key === event.target.value);
										if (!coin || coin.usable) {
											++flowRef.current; ++quoteRequestRef.current; ++addressRequestRef.current;
											setSelected(event.target.value); setQuote(null); setQuoteError(null); setQuoteLoading(false);
											setAddress(null); setQr(""); setAddressError(null); setAddressLoading(false);
										}
									}}
									style={{ ...nativeSelectStyle, ...inputStyle, paddingLeft: selected ? "48px" : "14px", color: selected ? DS.ink : DS.muted }}>
									<option value="">Select an asset and network</option>
									{coins.map(coin => <option key={coin.coin_key} value={coin.coin_key} disabled={!coin.usable} title={coin.unavailable_reason ?? undefined}>{coin.coin_name} · {coin.chain}{coin.coin_key === cheapestCoinKey ? " · Lowest network fee" : ""}{!coin.usable ? ` · Unavailable — ${coin.unavailable_reason || "Temporarily unavailable"}` : ` · $${formatMoney(coin.gas_fee_cents)} network fee`}</option>)}
								</select>
							</Box>
							{selectedCoin ? <Typo sx={{ color: DS.muted, fontSize: "13px", lineHeight: 1.4 }}>{selectedCoin.coin_key === cheapestCoinKey ? "Lowest available network fee · " : ""}${formatMoney(selectedCoin.gas_fee_cents)} network fee · ${formatMoney(selectedCoin.min_deposit_cents)} forwarding minimum</Typo> : <Typo sx={{ color: DS.muted, fontSize: "13px" }}>The asset and network must match the transfer.</Typo>}
						</VBox>
						{loading ? <Typo sx={{ color: DS.muted }}>Loading deposit options…</Typo> : !selected ? <HBox gap="10px" sx={{ alignItems: "center", padding: "14px", borderRadius: "12px", color: DS.muted, backgroundColor: DS.soft, border: `1px dashed ${DS.line}` }}><IconSpark /><Typo sx={{ color: DS.muted, fontSize: "14px" }}>Select an asset and network to calculate your top-up.</Typo></HBox> : <>
							<VBox gap="8px">
								<Typo sx={{ color: DS.text, fontSize: "13px", fontWeight: 600 }}>Amount to credit to your balance (USD)</Typo>
								<input aria-label="Amount to credit in USD" inputMode="decimal" placeholder="100.00" value={depositAmount} onChange={event => {
									++flowRef.current; ++quoteRequestRef.current; ++addressRequestRef.current;
									setDepositAmount(event.target.value); setQuote(null); setQuoteError(null); setQuoteLoading(false);
									setAddress(null); setQr(""); setAddressError(null); setAddressLoading(false);
								}} style={inputStyle} />
								{depositAmount && (amountCents === null || amountCents <= 0) ? <Box role="alert"><Typo sx={{ color: "#B91C1C", fontSize: "13px" }}>Enter an amount greater than zero, for example 100.00.</Typo></Box> : null}
								{quoteLoading ? <Typo sx={{ color: DS.muted, fontSize: "13px" }}>Calculating the current amount to send…</Typo> : null}
								{quoteError ? <VBox role="alert" gap="8px" sx={{ alignItems: "flex-start" }}><Typo sx={{ color: "#B91C1C", fontSize: "13px", lineHeight: 1.45 }}>{quoteError.message}</Typo><Button variant="secondarySmall" onClick={() => { if (amountCents && amountCents > 0) void requestQuote(selected, amountCents, flowRef.current); }}>Retry quote</Button></VBox> : null}
							</VBox>
							{quote ? <VBox gap="8px" aria-label="Deposit quote" sx={{ padding: "16px", borderRadius: "12px", backgroundColor: DS.soft, border: `1px solid ${DS.line}` }}>
								<FeeRow label="To your balance" value={`$${formatMoney(quote.credit_cents)}`} strong />
								<VBox gap="3px"><Typo sx={{ color: DS.muted, fontSize: "13px" }}>Send exactly</Typo><Typo sx={{ color: DS.ink, fontSize: "22px", fontWeight: 700, lineHeight: 1.2, overflowWrap: "anywhere" }}>{quote.send_display} {quote.coin_name}</Typo></VBox>
								<Box sx={{ height: "1px", backgroundColor: DS.line }} />
								{quote.percent_cents > 0 ? <FeeRow label="Top-up fee" value={`$${formatMoney(quote.percent_cents)}`} /> : null}
								{quote.gas_fee_cents > 0 ? <FeeRow label="Network fee" value={`$${formatMoney(quote.gas_fee_cents)}`} /> : null}
								{quote.fixed_cents > 0 ? <FeeRow label="Fixed fee" value={`$${formatMoney(quote.fixed_cents)}`} /> : null}
								{quote.issuer_fee_cents > 0 ? <FeeRow label="Issuer fee" value={`$${formatMoney(quote.issuer_fee_cents)}`} /> : null}
								{quote.note ? <Typo sx={{ color: DS.muted, fontSize: "13px", lineHeight: 1.45 }}>{quote.note}</Typo> : null}
								<Typo sx={{ color: DS.muted, fontSize: "12px" }}>Quoted {new Date(quote.quoted_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}. Prices and fees may change before your deposit is credited.</Typo>
								{quote.stale ? <Box role="alert"><Typo sx={{ color: "#92400E", fontSize: "13px" }}>Current prices could not be refreshed. Request a fresh quote before paying.</Typo></Box> : null}
								<Button variant="secondarySmall" onClick={() => { if (amountCents && amountCents > 0) void requestQuote(selected, amountCents, flowRef.current); }}>Refresh quote</Button>
							</VBox> : null}
							<Button disabled={!quote || quoteLoading || addressLoading || Boolean(quoteError)} onClick={() => void showAddress()}>{addressLoading ? "Loading address…" : "Show deposit address"}</Button>
							{addressError ? <VBox role="alert" gap="8px" sx={{ alignItems: "flex-start" }}><Typo sx={{ color: "#B91C1C", fontSize: "13px" }}>{addressError.message}</Typo><Button variant="secondarySmall" onClick={() => void showAddress()}>{addressError.recovery === "phone" ? "Complete card holder details" : addressError.recovery === "kyc" ? "Continue verification" : "Retry deposit address"}</Button></VBox> : null}
							{address ? <>
							<VBox gap="6px">
								<Typo sx={{ color: DS.text, fontSize: "13px", fontWeight: 600 }}>Your deposit address</Typo>
								<HBox gap="8px" sx={{ alignItems: "center", minWidth: 0, minHeight: "50px", padding: "4px 5px 4px 14px", borderRadius: "12px", border: `1px solid ${DS.line}`, backgroundColor: DS.soft, transition: "border-color .18s ease, box-shadow .18s ease", "&:focus-within": { borderColor: DS.accent, boxShadow: `0 0 0 3px ${DS.accentSoft}` } }}>
									<input readOnly aria-label="Deposit address" title={address.address} value={address.address} style={{ flex: 1, minWidth: 0, width: "100%", height: "38px", padding: 0, border: 0, outline: 0, background: "transparent", color: DS.ink, fontFamily: "ui-monospace, SFMono-Regular, Consolas, monospace", fontSize: "13px", lineHeight: 1.4, textOverflow: "ellipsis" }} />
									<Box aria-hidden sx={{ width: "1px", height: "24px", flexShrink: 0, backgroundColor: DS.line }} />
									<Button ariaLabel="Copy deposit address" variant="secondary" icon={<CopyIcon />} onClick={() => navigator.clipboard.writeText(address.address).then(() => toast.success("Address copied")).catch(() => toast.error("Could not copy address"))} sx={{ width: "40px", height: "40px", minWidth: "40px", padding: 0, border: 0, borderRadius: "10px", color: DS.accentInk, backgroundColor: DS.accentSoft, "&:hover": { color: "#fff", backgroundColor: DS.accent } }} />
								</HBox>
							</VBox>
							<Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 650 }}>Send exactly {quote?.send_display} {quote?.coin_name} to this address.</Typo>
							</> : null}</>}
					</VBox>
				</Section>
				<Section title="Scan to pay">
					<Box sx={{ minHeight: "240px", padding: "8px 0 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
						{qr ? <VBox center gap="12px"><Box sx={{ padding: "12px", borderRadius: "16px", backgroundColor: "#fff", border: `1px solid ${DS.line}`, boxShadow: DS.shadow, lineHeight: 0 }}><img src={qr} alt="Crypto deposit address QR code" width={180} height={180} style={{ maxWidth: "100%", height: "auto" }} /></Box><Typo sx={{ color: DS.muted, fontSize: "13px" }}>Scan with your wallet application</Typo></VBox> : <VBox center gap="12px" sx={{ textAlign: "center" }}><Box aria-hidden sx={{ width: "56px", height: "56px", borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: DS.accent, backgroundColor: DS.accentSoft }}><IconQr /></Box><Typo sx={{ color: DS.ink, fontWeight: 650 }}>Deposit QR code</Typo><Typo sx={{ color: DS.muted, maxWidth: "240px", lineHeight: 1.5, fontSize: "14px" }}>{qrPlaceholder}</Typo></VBox>}
					</Box>
				</Section>
				{address ? <Box sx={{ gridColumn: "1 / -1" }}><Callout tone="info" title={`Send only ${address.coin_name} via ${address.network || address.chain} to this address.`}>
					<VBox gap="3px">
						<span>Sending other assets or using a different network may result in permanent loss.</span>
						<span>{address.confirmations} confirmations · Send the exact quoted amount on the selected network.</span>
						{address.fee_note ? <span style={{ color: "#92400E" }}>{address.fee_note}</span> : null}
						{address.gas_note ? <span style={{ color: "#92400E" }}>{address.gas_note}</span> : null}
					</VBox>
				</Callout></Box> : null}
			</Box>
			<Section
				title="Recent deposits"
				action={<Button ariaLabel="Refresh" variant="secondary" icon={<RefreshIcon />} onClick={() => void refreshDeposits()} sx={{ width: "40px", height: "40px", minWidth: "40px", padding: 0, color: DS.accentInk, borderColor: DS.line, backgroundColor: DS.soft, borderRadius: "10px" }} />}
			>
				{!depositsLoaded ? <Typo sx={{ color: DS.muted, padding: "24px 0", lineHeight: 1.5 }}>{loading ? "Loading recent deposits…" : "Deposit history is temporarily unavailable."}</Typo> : !deposits.length ? <EmptyState icon={<IconDeposit />} title="No deposits" description="Pending and completed deposits will appear here." /> : <VBox>{deposits.map(deposit => {
					const amount = `${formatCryptoMinorUnits(deposit.tx_amount, deposit.coin_decimals)} ${deposit.coin_key.split("_")[0]}`;
					const sweepCopy = SWEEP_COPY[deposit.sweep_status] ?? deposit.sweep_status;
					return <HBox key={deposit.id} sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "16px", padding: "14px 0", borderTop: `1px solid ${DS.line}`, "&:first-of-type": { borderTop: 0 } }}>
						<HBox gap="12px" sx={{ alignItems: "flex-start", minWidth: 0 }}><Box aria-hidden sx={{ width: "38px", height: "38px", borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#047857", backgroundColor: "#ECFDF5", fontWeight: 700 }}>₮</Box><VBox gap="3px" sx={{ minWidth: 0 }}>
							<Typo sx={{ color: DS.ink, fontWeight: 600, fontSize: "14px" }}>{deposit.coin_key.split("_")[0]} · {deposit.chain_key}</Typo>
							<Box title={deposit.order_no}><Typo sx={{ color: DS.muted, fontSize: "13px" }}>{new Date(deposit.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</Typo></Box>
							{showsProcessingEstimate(deposit) ? <Typo sx={{ color: DS.muted, fontSize: "13px" }}>Payment detected. Your balance will be updated within 5–10 minutes.</Typo> : null}
							{deposit.sweep_status === "waiting" ? <Typo sx={{ color: "#92400E", fontSize: "13px" }}>Received {amount}. It will be credited after the address balance reaches the forwarding minimum.</Typo> : null}
							{deposit.sweep_status === "funding" && deposit.sweep_error ? <Typo sx={{ color: "#92400E", fontSize: "13px" }}>{deposit.sweep_error}</Typo> : null}
							{deposit.sweep_status === "failed" && deposit.sweep_error ? <Typo sx={{ color: "#B91C1C", fontSize: "13px" }}>{deposit.sweep_error}</Typo> : null}
							{deposit.credit_error ? <Typo sx={{ color: "#B91C1C", fontSize: "13px" }}>{deposit.credit_error}</Typo> : null}
						</VBox></HBox>
						<VBox gap="2px" sx={{ alignItems: "flex-end", flexShrink: 0 }}>
							<Typo sx={{ color: deposit.status === "credited" ? "#047857" : DS.ink, fontSize: "14px", fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>+ {formatPrivateMoney(deposit.received_amount, deposit.received_currency)}</Typo>
							<Typo sx={{ color: deposit.status === "credited" ? "#047857" : deposit.sweep_status === "failed" ? "#B91C1C" : DS.muted, fontSize: "13px", fontWeight: 600 }}>{deposit.status === "credited" ? "Completed" : sweepCopy}</Typo>
							{deposit.status === "credited" && deposit.fee_cents > 0 ? <Typo sx={{ color: DS.muted, fontSize: "12px" }}>Fee {formatMoney(deposit.fee_cents)} {deposit.received_currency}{deposit.gas_fee_cents > 0 ? ` · network ${formatMoney(deposit.gas_fee_cents)}` : ""}</Typo> : null}
						</VBox>
					</HBox>;
				})}</VBox>}
			</Section>
		</VBox>
	);
};

/** Numbered progress for the funding flow; purely presentational. */
const Stepper = ({ steps, active }: { steps: string[]; active: number }) => (
	<HBox gap="8px" role="list" aria-label="Funding steps" sx={{ alignItems: "center", flexWrap: "wrap" }}>
		{steps.map((label, index) => {
			const done = index < active;
			const current = index === active;
			return (
				<HBox key={label} role="listitem" gap="8px" sx={{ alignItems: "center", padding: "6px 12px 6px 6px", borderRadius: "999px", border: `1px solid ${current ? DS.accent : DS.line}`, backgroundColor: current ? DS.accentSoft : DS.surface }}>
					<Box aria-hidden sx={{ width: "22px", height: "22px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, color: done || current ? "#fff" : DS.muted, backgroundColor: done ? "#10B981" : current ? DS.accent : DS.soft }}>{done ? "✓" : index + 1}</Box>
					<Typo sx={{ fontSize: "13px", fontWeight: current ? 650 : 500, color: current ? DS.accentInk : DS.muted }}>{label}</Typo>
				</HBox>
			);
		})}
	</HBox>
);

const FeeRow = ({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) => <HBox sx={{ justifyContent: "space-between", gap: "12px" }}><Typo sx={{ color: strong ? DS.ink : DS.muted, fontSize: "13px", fontWeight: strong ? 650 : 500 }}>{label}</Typo><Typo sx={{ color: DS.ink, fontSize: "13px", fontWeight: strong ? 700 : 600, fontVariantNumeric: "tabular-nums" }}>{value}</Typo></HBox>;

const RefreshIcon = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 11a8 8 0 10-2.3 5.7M20 5v6h-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const CopyIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="8" y="8" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2" stroke="currentColor" strokeWidth="1.8" /></svg>;
const IconSpark = () => <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3l1.3 4.7L18 9l-4.7 1.3L12 15l-1.3-4.7L6 9l4.7-1.3L12 3zM18.5 15l.7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>;
const IconQr = () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v6h-2zM14 18h2v2h-2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>;
const IconDeposit = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 4v11m0 0l-4-4m4 4l4-4M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
